import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { FullScreenLoading } from '@backrs_ui/common-ui';

import { SEARCH_PAGE_CATEGORIES } from '@backrs_ui/grow-ui/src/pages/Search/search.types';
import { handleInitiateSearch } from '@backrs_ui/grow-ui/src/pages/Search/search.util';
import { useAppSelector, useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectSharedBlueprintData } from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';
import { selectUserSurveyResults } from '@backrs_ui/grow-ui/src/redux/userResponse/userResponse';

import { CareerPathDetailPage } from './CareerPathDetailPage';
import { CareerPathPage } from './CareerPathPage';
import { useCareerPathById } from './hooks/useCareerPathById';
import {
  getCareerPathCompensationsByPathId,
  getCareerPathSampleJobsByPathId,
  getCareerPathAIDisruptionByPathId,
} from './util';

type CareerPathPageContainerProps = Readonly<{
  isShared?: boolean;
}>;

export function CareerPathPageContainer({ isShared = false }: CareerPathPageContainerProps) {
  const { careerPathId } = useParams();
  const dispatch = useAppDispatch();

  const {
    careerPaths,
    careerCategories,
    careerPathCompensations,
    careerPathSampleJobs,
    careerTracks,
    careerPathAIDisruptions,
  } = useAppSelector((state) => state.careerPath);
  const surveyResults = useAppSelector((state) => selectUserSurveyResults(state));
  const sharedBlueprintData = useAppSelector(selectSharedBlueprintData);
  const { loadingMostRecentUserResponseSet } = useAppSelector((state) => state.userResponseSet);

  const careerPathSuggestions =
    isShared && sharedBlueprintData
      ? sharedBlueprintData.careerPathSuggestions
      : surveyResults?.careerPathSuggestions;

  // Use custom hook to find career path
  const careerPath = useCareerPathById(careerPathId, careerPaths);

  // Get compensation data for this career path
  const careerPathCompensationData = careerPath
    ? getCareerPathCompensationsByPathId(careerPath.id, careerPathCompensations)
    : [];

  const careerPathAIDisruption = getCareerPathAIDisruptionByPathId(
    careerPathAIDisruptions,
    careerPath?.id || 0,
  );

  // Get sample job data for this career path
  const careerPathSampleJobsData = getCareerPathSampleJobsByPathId(
    careerPath?.id || 0,
    careerPathSampleJobs,
    careerTracks,
  );

  const userHasBlueprint = useAppSelector(
    (state) => state.userResponseSet.mostRecentUserResponseSet?.user_responses,
  );

  // Initialize search when career path is found
  useEffect(() => {
    if (careerPath) {
      handleInitiateSearch({
        dispatch,
        currentSearchTerm: careerPath.name,
        pageNum: 1,
        items: [],
        searchPageCategory: SEARCH_PAGE_CATEGORIES.CAREER_PATHS,
        hasInitialSearchBeenMade: false,
      });
    }
  }, [dispatch, careerPath, careerPathId]);

  if (loadingMostRecentUserResponseSet) {
    return <FullScreenLoading />;
  }

  if (careerPath) {
    return (
      <CareerPathDetailPage
        careerPath={careerPath}
        careerPathCompensations={careerPathCompensationData}
        careerPathSampleJobs={careerPathSampleJobsData}
        careerPaths={careerPaths}
        careerPathSuggestions={careerPathSuggestions}
        careerPathAIDisruption={careerPathAIDisruption}
        isShared={isShared}
      />
    );
  }

  return (
    <CareerPathPage
      careerPathSuggestions={careerPathSuggestions}
      careerCategories={careerCategories}
      careerPaths={careerPaths}
      userHasBlueprint={isShared && sharedBlueprintData ? true : !!userHasBlueprint}
      isShared={isShared}
    />
  );
}
